import {
  CANVAS_CONFIG,
  GAME_PARAMS,
  isInsideCircle,
  type PhaseType,
} from '@beat-games/game-config';

export interface ReplayPosition {
  x: number;
  y: number;
  t: number;
}

export interface CircleSnapshot {
  x: number;
  y: number;
  radius: number;
  elapsed: number;
}

export interface ReplayData {
  sessionId: string;
  walletAddress: string;
  score: number;
  survivalTimeMs: number;
  precisionError: number;
  seed: number;
  phaseType: PhaseType;
  positions: ReplayPosition[];
  circleStates: CircleSnapshot[];
  startedAt: string;
}

export interface CircleState {
  x: number;
  y: number;
  radius: number;
}

export interface ReplayState {
  elapsed: number;
  duration: number;
  circle: CircleState;
  cursor: { x: number; y: number };
  isInside: boolean;
  isPlaying: boolean;
  playbackSpeed: number;
}

export class ReplayEngine {
  private replay: ReplayData | null = null;
  private isPlaying: boolean = false;
  private playbackSpeed: number = 1;
  private currentElapsed: number = 0;
  private lastFrameTime: number = 0;
  private animationFrameId: number | null = null;
  private onStateChange: ((state: ReplayState) => void) | null = null;

  load(replay: ReplayData): void {
    this.replay = replay;
    this.currentElapsed = 0;
    this.isPlaying = false;
    this.emitState();
  }

  private getCircleAtTime(elapsed: number): CircleState {
    if (!this.replay || this.replay.circleStates.length === 0) {
      return {
        x: CANVAS_CONFIG.width / 2,
        y: CANVAS_CONFIG.height / 2,
        radius: GAME_PARAMS.startRadius,
      };
    }

    const circleStates = this.replay.circleStates;

    // Find the circle states before and after the elapsed time
    let before: CircleSnapshot | null = null;
    let after: CircleSnapshot | null = null;

    for (let i = 0; i < circleStates.length; i++) {
      const state = circleStates[i];
      if (state && state.elapsed <= elapsed) {
        before = state;
      }
      if (state && state.elapsed >= elapsed && !after) {
        after = state;
        break;
      }
    }

    // If no state found, return first or last
    if (!before && !after) {
      const first = circleStates[0];
      if (first) {
        return { x: first.x, y: first.y, radius: first.radius };
      }
      return {
        x: CANVAS_CONFIG.width / 2,
        y: CANVAS_CONFIG.height / 2,
        radius: GAME_PARAMS.startRadius,
      };
    }

    if (!before) {
      return { x: after!.x, y: after!.y, radius: after!.radius };
    }

    if (!after || before.elapsed === after.elapsed) {
      return { x: before.x, y: before.y, radius: before.radius };
    }

    // Interpolate between the two states for smooth playback
    const t = (elapsed - before.elapsed) / (after.elapsed - before.elapsed);
    return {
      x: before.x + (after.x - before.x) * t,
      y: before.y + (after.y - before.y) * t,
      radius: before.radius + (after.radius - before.radius) * t,
    };
  }

  private getCursorAtTime(elapsed: number): { x: number; y: number } {
    if (!this.replay || this.replay.positions.length === 0) {
      return { x: CANVAS_CONFIG.width / 2, y: CANVAS_CONFIG.height / 2 };
    }

    const positions = this.replay.positions;

    // Normalize timestamps - first position timestamp is t=0
    const firstPos = positions[0];
    if (!firstPos) {
      return { x: CANVAS_CONFIG.width / 2, y: CANVAS_CONFIG.height / 2 };
    }
    const baseTime = firstPos.t;

    // Find positions before and after elapsed time
    let before: ReplayPosition | null = null;
    let after: ReplayPosition | null = null;

    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      if (!pos || pos.x === undefined || pos.y === undefined) continue;
      const relativeTime = pos.t - baseTime;
      if (relativeTime <= elapsed) {
        before = { x: pos.x, y: pos.y, t: relativeTime };
      }
      if (relativeTime >= elapsed && !after) {
        after = { x: pos.x, y: pos.y, t: relativeTime };
        break;
      }
    }

    if (!before) {
      return { x: firstPos.x ?? CANVAS_CONFIG.width / 2, y: firstPos.y ?? CANVAS_CONFIG.height / 2 };
    }

    if (!after || before.t === after.t) {
      return { x: before.x, y: before.y };
    }

    // Linear interpolation
    const t = (elapsed - before.t) / (after.t - before.t);
    return {
      x: before.x + (after.x - before.x) * t,
      y: before.y + (after.y - before.y) * t,
    };
  }

  play(): void {
    if (!this.replay || this.isPlaying) return;
    this.isPlaying = true;
    this.lastFrameTime = performance.now();
    this.emitState(); // Emit state immediately so UI updates
    this.tick();
  }

  pause(): void {
    this.isPlaying = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.emitState();
  }

  togglePlayPause(): void {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  setSpeed(speed: number): void {
    this.playbackSpeed = speed;
    this.emitState();
  }

  seek(elapsed: number): void {
    if (!this.replay) return;
    this.currentElapsed = Math.max(0, Math.min(elapsed, this.replay.survivalTimeMs));
    this.emitState();
  }

  seekPercent(percent: number): void {
    if (!this.replay) return;
    const elapsed = (percent / 100) * this.replay.survivalTimeMs;
    this.seek(elapsed);
  }

  restart(): void {
    this.currentElapsed = 0;
    this.emitState();
    if (this.isPlaying) {
      this.lastFrameTime = performance.now();
    }
  }

  setOnStateChange(callback: (state: ReplayState) => void): void {
    this.onStateChange = callback;
  }

  getState(): ReplayState {
    const circle = this.getCircleAtTime(this.currentElapsed);
    const cursor = this.getCursorAtTime(this.currentElapsed);
    const inside = isInsideCircle(cursor.x, cursor.y, circle.x, circle.y, circle.radius);

    return {
      elapsed: this.currentElapsed,
      duration: this.replay?.survivalTimeMs ?? GAME_PARAMS.maxDuration,
      circle,
      cursor,
      isInside: inside,
      isPlaying: this.isPlaying,
      playbackSpeed: this.playbackSpeed,
    };
  }

  private tick = (): void => {
    if (!this.isPlaying || !this.replay) return;

    const now = performance.now();
    const delta = (now - this.lastFrameTime) * this.playbackSpeed;
    this.lastFrameTime = now;

    this.currentElapsed += delta;

    // Check if replay finished
    if (this.currentElapsed >= this.replay.survivalTimeMs) {
      this.currentElapsed = this.replay.survivalTimeMs;
      this.isPlaying = false;
      this.emitState();
      return;
    }

    this.emitState();
    this.animationFrameId = requestAnimationFrame(this.tick);
  };

  private emitState(): void {
    if (this.onStateChange) {
      this.onStateChange(this.getState());
    }
  }

  destroy(): void {
    this.pause();
    this.replay = null;
    this.onStateChange = null;
  }
}
